import React, { useState, useEffect } from 'react';
import { X, Bell, AlertCircle } from 'lucide-react';
import { Modal, Toggle, Autocomplete } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { notificationConfigs, accounts, libraries } from '../../services/apiService';
import eventTypes from '../../../../shared/eventTypes.json';

const EVENT_TYPE_OPTIONS = eventTypes.NOTIFICATION_EVENTS.map(e => ({ value: e.value, label: e.label }));

const NotificationConfigModal = ({ isOpen, onClose, config, onSaved, existingConfigs = [] }) => {
  const isEdit = Boolean(config?.id);

  const [form, setForm] = useState({
    event_type: '',
    account_id: '',
    credential_set_id: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [credentialSets, setCredentialSets] = useState([]);
  const [toolError, setToolError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && config) {
      setForm({
        event_type: config.event_type || '',
        account_id: config.account_id || '',
        credential_set_id: config.credential_set_id != null ? String(config.credential_set_id) : '',
        is_active: config.is_active !== undefined ? config.is_active : true,
      });
    } else {
      setForm({ event_type: '', account_id: '', credential_set_id: '', is_active: true });
    }

    loadAllDiscordCredentials();
  }, [isOpen]);

  const loadAllDiscordCredentials = async () => {
    setToolError(null);
    setLoadingCredentials(true);
    setCredentialSets([]);
    try {
      const tools = await libraries.getToolByName('Discord');
      const list = Array.isArray(tools) ? tools : (tools?.data || []);
      const tool = list.find(t => t.name?.toLowerCase() === 'discord') || list[0] || null;
      if (!tool) {
        setToolError('Tool "Discord" not found in libraries service. Please add it first.');
        return;
      }
      const sets = await accounts.getAllCredentialsByToolId(tool.id);
      setCredentialSets(Array.isArray(sets) ? sets : []);
    } catch {
      setToolError('Failed to load Discord credentials.');
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_type || !form.credential_set_id) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      const selectedSet = credentialSets.find(s => String(s.id) === String(form.credential_set_id));
      const payload = {
        event_type: form.event_type,
        account_id: Number(selectedSet?.owner_id || form.account_id),
        credential_set_id: Number(form.credential_set_id),
        is_active: form.is_active,
      };
      if (isEdit) {
        await notificationConfigs.update(config.id, payload);
        toast.success('Updated');
      } else {
        await notificationConfigs.create(payload);
        toast.success('Created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // При создании — исключаем event_type, которые уже заняты
  // При редактировании — разрешаем текущий тип, но блокируем остальные занятые
  const takenEventTypes = new Set(
    existingConfigs
      .filter(c => !isEdit || c.id !== config?.id)
      .map(c => c.event_type)
  );
  const eventTypeOptions = EVENT_TYPE_OPTIONS.map(opt => ({
    ...opt,
    disabled: takenEventTypes.has(opt.value),
    label: takenEventTypes.has(opt.value) ? `${opt.label} (already configured)` : opt.label,
  }));

  const credentialOptions = credentialSets.map(s => ({
    value: String(s.id),
    label: s.account_name
      ? `${s.account_name} — ${s.name || `Set #${s.id}`}`
      : (s.name || `Set #${s.id}`),
  }));

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={saving ? undefined : onClose}
      closeOnBackdropClick={!saving}
      closeOnEscape={!saving}
      showCloseButton={false}
      size="medium"
      style={{
        width: 'calc(100% - 1rem)',
        maxWidth: '32rem',
        padding: 0,
        overflow: 'hidden',
      }}
      backdropStyle={{ padding: '0.5rem' }}
    >
      <div className="max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-xl bg-light-bg-primary shadow-2xl dark:bg-dark-bg-secondary sm:max-h-[calc(100dvh-2rem)]">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-light-border px-4 py-4 dark:border-dark-border sm:px-6">
          <div className="flex items-center gap-2.5">
            <Bell size={18} className="text-violet-500" />
            <h2 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary">
              {isEdit ? 'Edit Notification Config' : 'Add Notification Config'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close notification configuration"
            className="p-1.5 text-light-text-tertiary hover:text-light-text-primary dark:text-dark-text-tertiary dark:hover:text-dark-text-primary rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tool error banner */}
        {toolError && (
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            <span>{toolError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-6">
          {/* Event type */}
          <div>
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1.5">
              Event Type <span className="text-red-500">*</span>
            </label>
            <Autocomplete
              clearable
              fullWidth
              options={eventTypeOptions}
              value={form.event_type}
              onChange={val => setForm(prev => ({ ...prev, event_type: val || '' }))}
              placeholder="Select event type..."
            />
          </div>

          {/* Credential Set */}
          <div>
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1.5">
              Discord Credential Set <span className="text-red-500">*</span>
            </label>
            {loadingCredentials ? (
              <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-2">
                Loading Discord credentials...
              </div>
            ) : !toolError ? (
              <>
                <Autocomplete
                  clearable
                  fullWidth
                  options={credentialOptions}
                  value={form.credential_set_id}
                  onChange={val => setForm(prev => ({ ...prev, credential_set_id: val || '' }))}
                  placeholder="Select Discord credential set..."
                />
                {credentialOptions.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    No Discord credential sets found. Create one in account-service first.
                  </p>
                )}
              </>
            ) : null}
          </div>

          {/* Is Active */}
          <Toggle
            checked={form.is_active}
            onChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
            label="Active"
            color="primary"
          />

          {/* Footer */}
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary border border-light-border dark:border-dark-border rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !!toolError || loadingCredentials}
              className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default NotificationConfigModal;
